import type { IconName } from "@/components/ui/icon";

/**
 * Shared source of truth for the sequence detail tabs (Steps /
 * Enrollments / Tasks / Inbox). Each tab is its own route —
 * `/jobs/{jobId}/sequences/{sequenceId}/{tab}` — consumed by both the
 * on-page `SequenceTabsNav` strip (desktop, `lg` and up) and the mobile
 * drawer's `SequencePanel` drill-down, so the two surfaces never drift.
 */
export const SEQUENCE_TAB_VALUES = [
  "steps",
  "enrollments",
  "tasks",
  "inbox",
] as const;

export type SequenceTabValue = (typeof SEQUENCE_TAB_VALUES)[number];

export interface SequenceTabItem {
  value: SequenceTabValue;
  label: string;
  icon: IconName;
}

export const SEQUENCE_TAB_ITEMS: SequenceTabItem[] = [
  { value: "steps", label: "Steps", icon: "IconAgent" },
  { value: "enrollments", label: "Enrollments", icon: "IconTeam" },
  { value: "tasks", label: "Tasks", icon: "IconTodos" },
  { value: "inbox", label: "Inbox", icon: "IconEmail1" },
];

export interface SequenceRouteMatch {
  jobId: string;
  sequenceId: string;
  activeTab: SequenceTabValue;
}

const SEQUENCE_ROUTE_RE = new RegExp(
  `^/jobs/([^/]+)/sequences/([^/]+)/(${SEQUENCE_TAB_VALUES.join("|")})(?:/|$)`,
);

/**
 * Matches a pathname against a sequence detail tab route
 * (`/jobs/{jobId}/sequences/{sequenceId}/{tab}`) and extracts the ids
 * and active tab. Returns `null` for everything else, including the
 * bare `/jobs/{jobId}/sequences/{sequenceId}` route (which redirects
 * server-side to `/steps`) and the full-screen enrollment preview
 * route (`.../enroll/preview/...`), neither of which render the
 * sequence tab chrome.
 */
export function matchSequenceRoute(
  pathname: string | null | undefined,
): SequenceRouteMatch | null {
  if (!pathname) return null;
  const match = SEQUENCE_ROUTE_RE.exec(pathname);
  if (!match) return null;
  const [, jobId, sequenceId, activeTab] = match;
  return { jobId, sequenceId, activeTab: activeTab as SequenceTabValue };
}
